<template>
  <div id="app-container">
    <TarjaDeAcessoComoOutro />

    <main class="conteudo-principal">
      <router-view />
    </main>

    <ConfirmDialog :draggable="false" />
    <Toast />
  </div>
</template>

<script setup lang="ts">
import TarjaDeAcessoComoOutro from "@/components/TarjaDeAcessoComoOutro.vue";
import { useThemeStore } from "@/stores/ThemeStore";
import ConfirmDialog from "primevue/confirmdialog";
import Toast from "primevue/toast";
import { onMounted } from "vue";

const themeStore = useThemeStore();

onMounted(async () => {
  themeStore.initTheme();
});
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Manrope:wght@200..800&family=Raleway:ital,wght@0,100..900;1,100..900&display=swap');

html,
body {
  height: 100%;
  margin: 0;
  padding: 0;
  overflow-x: hidden;
  /* Previne scroll horizontal desnecessário */
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
  font-family: 'Manrope', sans-serif;
}

#app {
  height: 100%;
  margin: 0;
  padding: 0;
}

#app-container {
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
}

.conteudo-principal {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  background-color: var(--background-color);
}

:root {
  --primary-color: #6b7280;
  --secondary-color: #9ca3af;
  --background-color: #F3F3F3;
  --surface-color: #ffffff;
  --text-color: #1f2937;
  --text-secondary: #6b7280;
  --border-color: #e5e7eb;
  --shadow-color: rgba(0, 0, 0, 0.05);

  /* Primary Button Colors */
  --primary-button-bg: #822133;
  --primary-button-hover-bg: #822133;
}

.dark {
  --primary-color: #9ca3af;
  --secondary-color: #6b7280;
  --background-color: #111827;
  --surface-color: #1f2937;
  --text-color: #f9fafb;
  --text-secondary: #9ca3af;
  --border-color: #374151;
  --shadow-color: rgba(0, 0, 0, 0.2);
}

html,
body {
  background-color: var(--background-color);
  color: var(--text-color);
  transition: background-color 0.3s ease, color 0.3s ease;
}

#app-container {
  background-color: var(--background-color);
}

.conteudo-principal {
  background-color: var(--background-color);
  color: var(--text-color);
}

.p-component {
  background-color: var(--surface-color);
  color: var(--text-color);
  border-color: var(--border-color);
}

.dark .p-button {
  background-color: var(--surface-color);
  border-color: var(--border-color);
  color: var(--text-color);
}

.dark .p-button:hover {
  background-color: var(--border-color);
  border-color: var(--border-color);
}

.dark .p-button.p-button-text,
.dark .p-button.p-button-text:hover {
  background-color: transparent;
  border-color: transparent;
}

.p-toolbar {
  background-color: var(--surface-color);
  border: none;
  box-shadow: none;
}

.p-drawer {
  background-color: var(--surface-color);
  color: var(--text-color);
}

.p-drawer .p-drawer-header {
  background-color: var(--surface-color);
  color: var(--text-color);
  border-bottom: 1px solid var(--border-color);
}

.p-drawer .p-drawer-content {
  background-color: var(--surface-color);
  color: var(--text-color);
}

.p-drawer .p-drawer-header .p-drawer-title {
  color: var(--text-color);
}

.p-drawer .p-drawer-header .p-drawer-close {
  color: var(--text-color);
}

.p-drawer .p-drawer-header .p-drawer-close:hover {
  background-color: var(--border-color);
}

.p-drawer h1,
.p-drawer h2,
.p-drawer h3,
.p-drawer h4,
.p-drawer h5,
.p-drawer h6 {
  color: var(--text-color);
}

.p-drawer small {
  color: var(--text-secondary);
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.p-dialog {
  background-color: var(--surface-color);
  color: var(--text-color);
}

.p-toast {
  background-color: transparent !important;
  box-shadow: none !important;
  border: none !important;
}

/* Custom solid Toast messages overrides for high readability */
.p-toast-message {
  backdrop-filter: none !important;
  opacity: 1 !important;
  border-width: 1px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

/* Quebra de linha no texto vira linha nova (lista de alunos que ficaram de fora da sincronização). */
.p-toast-detail {
  white-space: pre-line;
}

/* Light mode (default) */
.p-toast-message-info {
  background-color: #e0f2fe !important;
  border-color: #bae6fd !important;
}

.p-toast-message-info .p-toast-summary,
.p-toast-message-info .p-toast-message-icon,
.p-toast-message-info .p-toast-close-icon {
  color: #0369a1 !important;
}

.p-toast-message-info .p-toast-detail {
  color: #075985 !important;
}

.p-toast-message-success {
  background-color: #dcfce7 !important;
  border-color: #bbf7d0 !important;
}

.p-toast-message-success .p-toast-summary,
.p-toast-message-success .p-toast-message-icon,
.p-toast-message-success .p-toast-close-icon {
  color: #15803d !important;
}

.p-toast-message-success .p-toast-detail {
  color: #166534 !important;
}

.p-toast-message-warn {
  background-color: #fef9c3 !important;
  border-color: #fef08a !important;
}

.p-toast-message-warn .p-toast-summary,
.p-toast-message-warn .p-toast-message-icon,
.p-toast-message-warn .p-toast-close-icon {
  color: #a16207 !important;
}

.p-toast-message-warn .p-toast-detail {
  color: #854d0e !important;
}

.p-toast-message-error {
  background-color: #fee2e2 !important;
  border-color: #fecaca !important;
}

.p-toast-message-error .p-toast-summary,
.p-toast-message-error .p-toast-message-icon,
.p-toast-message-error .p-toast-close-icon {
  color: #b91c1c !important;
}

.p-toast-message-error .p-toast-detail {
  color: #991b1b !important;
}

/* Dark mode overrides */
.dark .p-toast-message-info {
  background-color: #082f49 !important;
  border-color: #0c4a6e !important;
}

.dark .p-toast-message-info .p-toast-summary,
.dark .p-toast-message-info .p-toast-message-icon,
.dark .p-toast-message-info .p-toast-close-icon {
  color: #38bdf8 !important;
}

.dark .p-toast-message-info .p-toast-detail {
  color: #bae6fd !important;
}

.dark .p-toast-message-success {
  background-color: #052e16 !important;
  border-color: #14532d !important;
}

.dark .p-toast-message-success .p-toast-summary,
.dark .p-toast-message-success .p-toast-message-icon,
.dark .p-toast-message-success .p-toast-close-icon {
  color: #4ade80 !important;
}

.dark .p-toast-message-success .p-toast-detail {
  color: #bbf7d0 !important;
}

.dark .p-toast-message-warn {
  background-color: #422006 !important;
  border-color: #713f12 !important;
}

.dark .p-toast-message-warn .p-toast-summary,
.dark .p-toast-message-warn .p-toast-message-icon,
.dark .p-toast-message-warn .p-toast-close-icon {
  color: #facc15 !important;
}

.dark .p-toast-message-warn .p-toast-detail {
  color: #fef08a !important;
}

.dark .p-toast-message-error {
  background-color: #450a0a !important;
  border-color: #7f1d1d !important;
}

.dark .p-toast-message-error .p-toast-summary,
.dark .p-toast-message-error .p-toast-message-icon,
.dark .p-toast-message-error .p-toast-close-icon {
  color: #f87171 !important;
}

.dark .p-toast-message-error .p-toast-detail {
  color: #fecaca !important;
}


.dark .tox-edit-area__iframe body img:not([src*="latex.codecogs.com"]) {
  background-color: #374151 !important;
  background-image: none !important;
}

.dark .tox-edit-area iframe body img:not([src*="latex.codecogs.com"]) {
  background-color: #374151 !important;
  background-image: none !important;
}

.dark .mce-content-body img:not([src*="latex.codecogs.com"]) {
  background-color: #374151 !important;
  background-image: none !important;
}

.dark .img-paragraph {
  background-color: #374151 !important;
  border-color: #6b7280 !important;
}

.p-checkbox-checked .p-checkbox-box {
  border-color: #822133 !important;
  background: #822133 !important;
}

[data-correction-wrapper][data-status="pendente"] {
  outline: 1px dashed #d48806;
  background: #fff4ce;
}

[data-correction-wrapper] {
  position: relative;
}

[data-correction-wrapper]::after {
  content: attr(data-correction-uuid);
  position: absolute;
  top: -10px;
  right: -10px;
  font-size: 10px;
  background: #d48806;
  color: #fff;
  padding: 2px 4px;
  border-radius: 3px;
}

.dark-mode img:not([src*="latex.codecogs.com"]) {
  background-color: #374151 !important;
  background-image: none !important;
}

.dark-mode .img-paragraph {
  background-color: #374151 !important;
  border-color: #6b7280 !important;
}

:not(.dark) .tox-edit-area__iframe body img:not([src*="latex.codecogs.com"]) {
  background-color: transparent !important;
  background-image: none !important;
}

:not(.dark) .tox-edit-area iframe body img:not([src*="latex.codecogs.com"]) {
  background-color: transparent !important;
  background-image: none !important;
}

:not(.dark) .mce-content-body img:not([src*="latex.codecogs.com"]) {
  background-color: transparent !important;
  background-image: none !important;
}

:not(.dark) .img-paragraph {
  background-color: #fafafa !important;
  border-color: #dddddd !important;
}

:not(.dark-mode) img:not([src*="latex.codecogs.com"]) {
  background-color: transparent !important;
  background-image: none !important;
}

:not(.dark-mode) .img-paragraph {
  background-color: #fafafa !important;
  border-color: #dddddd !important;
}

/* Admin dark-mode normalization: avoid hard-coded light surfaces in admin tables/cards. */
.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) {
  background: var(--background-color) !important;
  color: var(--text-color) !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) :is(.module-card,
  .bg-white,
  .p-card,
  .p-tabview,
  .p-tabview-panels,
  .p-tabview-tablist,
  .p-datatable,
  .p-datatable-header,
  .p-datatable-wrapper,
  .p-datatable-table,
  .p-select,
  .p-inputtext,
  .p-password input,
  .p-multiselect,
  .p-dropdown,
  .p-paginator,
  .header-section,
  .table-section,
  .filters-section,
  .surface-50,
  .bg-orange-50),
.dark :is(.p-dialog, .p-dialog .p-dialog-header, .p-dialog .p-dialog-content, .p-dialog .p-dialog-footer) {
  background: var(--surface-color) !important;
  color: var(--text-color) !important;
  border-color: var(--border-color) !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) .p-datatable :is(.p-datatable-thead > tr > th, .p-datatable-tbody > tr, .p-datatable-tbody > tr > td, .p-datatable-tfoot > tr > td) {
  background: var(--surface-color) !important;
  color: var(--text-color) !important;
  border-color: var(--border-color) !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) :is(.p-datatable .p-datatable-tbody > tr:hover,
  .p-datatable .p-datatable-tbody > tr.p-row-even:hover,
  .p-datatable .p-datatable-tbody > tr.p-row-odd:hover,
  .p-paginator .p-paginator-page:not(.p-disabled):hover,
  .p-paginator .p-paginator-prev:not(.p-disabled):hover,
  .p-paginator .p-paginator-next:not(.p-disabled):hover,
  .p-paginator .p-paginator-first:not(.p-disabled):hover,
  .p-paginator .p-paginator-last:not(.p-disabled):hover) {
  background: var(--border-color) !important;
  color: var(--text-color) !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) .p-datatable .p-datatable-tbody>tr:is(.p-row-even, .p-row-odd) {
  background: var(--surface-color) !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) :is(.p-column-title,
  .p-sortable-column-icon,
  .p-paginator .p-icon,
  .p-select-dropdown .p-icon,
  .p-select-option-dropdown-icon,
  .p-select-label,
  .p-multiselect-label,
  .text-color,
  .text-color-secondary,
  .text-muted,
  .card-count,
  .curso-codigo,
  .curso-info,
  code) {
  color: var(--text-color) !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) :is(.text-color-secondary, .text-muted, .card-count, .curso-codigo, .curso-info) {
  color: var(--text-secondary) !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) :is(code, .p-tag.p-tag-secondary, .auth-badge, .status-badge) {
  background: color-mix(in srgb, var(--surface-color) 80%, var(--border-color)) !important;
  color: var(--text-color) !important;
  border: 1px solid var(--border-color) !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) .auth-badge.auth-ldap {
  background: color-mix(in srgb, #2563eb 22%, var(--surface-color)) !important;
  color: #bfdbfe !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) :is(.auth-badge.auth-local, .status-badge.status-ativo) {
  background: color-mix(in srgb, #16a34a 20%, var(--surface-color)) !important;
  color: #bbf7d0 !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) :is(.status-badge.status-inativo, .status-badge.status-desligado, .status-badge.status-cancelado, .status-badge.status-excluido, .status-badge.status-excluído) {
  background: color-mix(in srgb, #dc2626 20%, var(--surface-color)) !important;
  color: #fecaca !important;
}

.dark :is(.admin-layout,
  .admin-dashboard,
  .admin-eduplay-view,
  .user-manager,
  .curso-manager,
  .template-manager,
  .template-metadata-manager,
  .role-manager,
  .permission-manager,
  .professores-manager,
  .alunos-manager,
  .coordenadores-view,
  .secretarias-view,
  .logs-view) :is(.status-badge.status-pendente, .bg-orange-50, .text-orange-600, .text-orange-800) {
  background: color-mix(in srgb, #d97706 18%, var(--surface-color)) !important;
  color: #fed7aa !important;
}
</style>
